"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import Select from "react-select";
import FaultTicketService, {
  FAULT_TYPE_LABEL,
  FAULT_STATUS_LABEL,
  FaultReportResponse,
  FaultReportBranch,
  FaultReportEmployee,
  formatDateTime,
  formatMinutes,
  getOverdueMinutes,
} from "../services/fault-ticket-service";

interface SimpleUser {
  id: number;
  name: string;
}

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// Định dạng số: dấu phẩy ngăn nghìn, KHÔNG thập phân (20300000 → "20,300,000"; 50 → "50").
const numberFmt = new Intl.NumberFormat("es-US", { maximumFractionDigits: 0 });
const fmt = (v: number) => numberFmt.format(Number(v) || 0);
const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};
const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  return { from: fmt(firstDay), to: fmt(now) };
};

// Bảng màu cho biểu đồ tỷ lệ loại lỗi.
const TYPE_COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#64748b"];

const FaultTicketReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.from);
  const [endDate, setEndDate] = useState(defaults.to);
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | "">("");
  const [report, setReport] = useState<FaultReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<SimpleUser[]>([]);
  const [detailEmp, setDetailEmp] = useState<{ branchName: string; employee: FaultReportEmployee } | null>(null);

  const { fetch: fetchReport } = FaultTicketService.report();
  const { fetch: listBranches } = BranchService.myBranches();
  const { request: listUsers } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.USER_SERVICE });

  const fetchRef = useRef(fetchReport);
  const branchRef = useRef(listBranches);
  const userRef = useRef(listUsers);
  useEffect(() => { fetchRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);
  useEffect(() => { userRef.current = listUsers; }, [listUsers]);

  useEffect(() => {
    (async () => {
      const res = await branchRef.current();
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const res = await userRef.current({
        endPoint: API_USER_ROUTER.API_LIST,
        data: { limit: 500, status: 1, company_id: 2000 },
      });
      if (res && isSuccess(res)) setAllUsers(extractList(res.data));
    })();
  }, []);

  useEffect(() => {
    const myBranchIds = new Set(branches.map((b: any) => Number(b.id)));
    const inScope = (u: any) =>
      u.is_all_branch === 1 || (u.branches || []).some((b: any) => myBranchIds.has(Number(b.id)));
    const filtered = allUsers.filter((u: any) => {
      if (!inScope(u)) return false;
      if (!selectedBranchId) return true;
      return u.is_all_branch === 1 || (u.branches || []).some((b: any) => Number(b.id) === Number(selectedBranchId));
    });
    setEmployees(filtered.map((u: any) => ({ id: u.id, name: u.name })));
    setSelectedEmployeeId("");
  }, [selectedBranchId, allUsers, branches]);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (selectedEmployeeId) params.employee_id = selectedEmployeeId;
      const res = await fetchRef.current(params);
      if (res && isSuccess(res)) setReport(res.data as FaultReportResponse);
    } catch (e) {
      console.error("Failed to load fault report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedBranchId, selectedEmployeeId]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const summary = report?.summary;
  const reportBranches = useMemo(() => report?.branches ?? [], [report]);

  // Biểu đồ cột theo chi nhánh: 2 cột (tổng tiền + tổng phiếu), 2 trục để không lệch tỷ lệ.
  const branchChart = useMemo(() => {
    const cats = reportBranches.map((b) => b.branch_name);
    const amounts = reportBranches.map((b) => b.total_amount);
    const counts = reportBranches.map((b) => b.total_tickets);
    const points = reportBranches.map((b) => b.total_points);
    const options: any = {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
      // Giới hạn bề rộng cột (không để quá to khi ít chi nhánh).
      plotOptions: { bar: { columnWidth: reportBranches.length <= 2 ? "22%" : "45%", borderRadius: 4, maxBarWidth: 48 } },
      colors: ["#ef4444", "#3b82f6"],
      dataLabels: { enabled: false }, // ẩn nhãn trên cột, thông tin đầy đủ ở tooltip
      xaxis: { categories: cats },
      yaxis: [
        { title: { text: "Tổng tiền (đ)" }, labels: { formatter: (v: number) => fmt(v) } },
        { opposite: true, title: { text: "Số phiếu" }, labels: { formatter: (v: number) => `${Math.round(v)}` } },
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number, opts: any) =>
            opts.seriesIndex === 0
              ? `${fmt(val)}đ (${points[opts.dataPointIndex]} điểm)`
              : `${val} phiếu`,
        },
      },
      legend: { position: "top" },
    };
    const series = [
      { name: "Tổng tiền", data: amounts },
      { name: "Tổng phiếu", data: counts },
    ];
    return { options, series };
  }, [reportBranches]);

  // Biểu đồ tỷ lệ theo loại lỗi (gộp toàn báo cáo). % làm tròn, KHÔNG thập phân.
  const typeChart = useMemo(() => {
    const rows = report?.by_type ?? [];
    const series = rows.map((r) => r.count);
    const labels = rows.map((r) => FAULT_TYPE_LABEL[r.fault_type] || `Loại ${r.fault_type}`);
    const options: any = {
      chart: { type: "pie", fontFamily: "Roboto, sans-serif" },
      labels,
      colors: TYPE_COLORS,
      dataLabels: { enabled: true, formatter: (val: number) => `${Math.round(val)}%` },
      legend: {
        position: "bottom",
        formatter: (name: string, opts: any) => {
          const arr: number[] = opts.w.globals.series;
          const total = arr.reduce((a, b) => a + b, 0) || 1;
          return `${name}  ${Math.round((arr[opts.seriesIndex] / total) * 100)}%`;
        },
      },
      tooltip: { y: { formatter: (val: number) => `${val} phiếu` } },
      stroke: { width: 1 },
    };
    return { series, options, has: rows.length > 0 };
  }, [report]);

  return (
    <div className="space-y-6">
      {/* Header + Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Báo cáo phiếu lỗi / phạt</h1>
              <p className="text-sm text-gray-500 mt-0.5">Tổng hợp điểm & tiền phạt theo chi nhánh và nhân sự</p>
            </div>
          </div>
          <button
            onClick={loadReport}
            disabled={isLoading}
            className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-colors"
          >
            <svg className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Từ ngày</label>
              <DateInputCustom type="date" value={toDMY(startDate)} onChange={(d) => setStartDate(toYMD(d ?? ""))} className="w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Đến ngày</label>
              <DateInputCustom type="date" value={toDMY(endDate)} onChange={(d) => setEndDate(toYMD(d ?? ""))} className="w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh</label>
              <select value={selectedBranchId} onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-gray-300 focus:border-gray-400 outline-none bg-white transition-colors">
                <option value="">Tất cả chi nhánh phụ trách</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Nhân viên</label>
              <Select
                placeholder="Tất cả nhân viên"
                options={employees.map((u) => ({ label: u.name, value: u.id }))}
                value={
                  selectedEmployeeId
                    ? {
                        label: employees.find((u) => u.id === selectedEmployeeId)?.name,
                        value: selectedEmployeeId,
                      }
                    : null
                }
                onChange={(option: any) => setSelectedEmployeeId(option ? option.value : "")}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.75rem",
                    borderColor: "#e5e7eb",
                    minHeight: "42px",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#d1d5db" },
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: "0 8px",
                  }),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500">Tổng phiếu</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.total_tickets ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500">Tổng điểm phạt</p>
          <p className="text-2xl font-bold text-rose-600 mt-1">{fmt(summary?.total_points ?? 0)} <span className="text-sm font-normal text-gray-400">điểm</span></p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500">Tổng tiền phạt</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(summary?.total_amount ?? 0)} <span className="text-sm font-normal text-gray-400">đ</span></p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500">Nhân viên bị phạt</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.employee_count ?? 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Theo chi nhánh</h2>
          {reportBranches.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">Không có dữ liệu</div>
          ) : (
            <ReactApexChart options={branchChart.options} series={branchChart.series} type="bar" height={300} />
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Tỷ lệ theo loại lỗi</h2>
          {!typeChart.has ? (
            <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">Không có dữ liệu</div>
          ) : (
            <ReactApexChart options={typeChart.options} series={typeChart.series} type="pie" height={300} />
          )}
        </div>
      </div>

      {/* Per-branch tables */}
      {reportBranches.map((b) => (
        <BranchBlock
          key={b.branch_id}
          branch={b}
          onView={(emp) => setDetailEmp({ branchName: b.branch_name, employee: emp })}
        />
      ))}

      {!isLoading && reportBranches.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400">
          Không có phiếu lỗi trong khoảng thời gian đã chọn.
        </div>
      )}

      {detailEmp && (
        <EmployeeFaultModal
          branchName={detailEmp.branchName}
          employee={detailEmp.employee}
          onClose={() => setDetailEmp(null)}
        />
      )}
    </div>
  );
};

// ===== Khối 1 chi nhánh: bảng nhân sự; bấm "Xem" mở modal phiếu chi tiết =====
const BranchBlock: React.FC<{
  branch: FaultReportBranch;
  onView: (emp: FaultReportEmployee) => void;
}> = ({ branch, onView }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900">{branch.branch_name}</h2>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">{fmt(branch.total_tickets)} phiếu</span>
          <span className="font-semibold text-rose-600">{fmt(branch.total_points)} điểm</span>
          <span className="font-semibold text-gray-900">{fmt(branch.total_amount)}đ</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-gray-600">
              <th className="px-4 py-3 text-left font-semibold min-w-[180px]">Nhân viên</th>
              <th className="px-4 py-3 text-center font-semibold">Số phiếu</th>
              <th className="px-4 py-3 text-right font-semibold text-rose-600">Điểm phạt</th>
              <th className="px-4 py-3 text-right font-semibold">Tiền phạt</th>
              <th className="px-4 py-3 text-center font-semibold w-24">Chi tiết</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {branch.employees.map((e) => (
              <tr key={e.employee_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{e.employee_name}</td>
                <td className="px-4 py-3 text-center">{fmt(e.total_tickets)}</td>
                <td className="px-4 py-3 text-right font-semibold text-rose-600">{fmt(e.total_points)}</td>
                <td className="px-4 py-3 text-right">{fmt(e.total_amount)}đ</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onView(e)}
                    className="text-xs px-3 py-1 rounded-full border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    Xem
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===== Modal: danh sách phiếu lỗi chi tiết của 1 nhân viên =====
const EmployeeFaultModal: React.FC<{
  branchName: string;
  employee: FaultReportEmployee;
  onClose: () => void;
}> = ({ branchName, employee, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-[900px] max-w-[95vw] max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{employee.employee_name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {branchName} · {fmt(employee.total_tickets)} phiếu ·{" "}
              <span className="text-rose-600 font-medium">{fmt(employee.total_points)} điểm</span> ·{" "}
              <span className="text-gray-900 font-medium">{fmt(employee.total_amount)}đ</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="overflow-auto p-4">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="text-gray-600 border-b border-gray-100">
                <th className="px-3 py-2 text-left font-semibold">Mã phiếu</th>
                <th className="px-3 py-2 text-left font-semibold">Loại lỗi / Mốc</th>
                <th className="px-3 py-2 text-center font-semibold">Đơn</th>
                <th className="px-3 py-2 text-right font-semibold">Vượt quy định</th>
                <th className="px-3 py-2 text-right font-semibold text-rose-600">Điểm</th>
                <th className="px-3 py-2 text-right font-semibold">Tiền</th>
                <th className="px-3 py-2 text-center font-semibold">Trạng thái</th>
                <th className="px-3 py-2 text-center font-semibold">Ngày tạo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {employee.tickets.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-800">{t.code}</td>
                  <td className="px-3 py-2">
                    <div>{FAULT_TYPE_LABEL[t.fault_type] || t.fault_type}</div>
                    {t.milestone_name && (
                      <div className="text-[11px] text-gray-400">
                        {t.milestone_name}
                        {t.escalation_level ? ` · Lần ${t.escalation_level}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{t.order_code || "—"}</td>
                  {/* Phiếu tay không có mốc SLA → để trống; hover xem cửa sổ thực hiện bước. */}
                  <td
                    className="px-3 py-2 text-right text-rose-600"
                    title={
                      t.sla_from_at
                        ? `Thực hiện: ${formatDateTime(t.sla_from_at)} → ${
                            t.sla_to_at ? formatDateTime(t.sla_to_at) : "chưa hoàn thành"
                          }` +
                          (t.sla_elapsed_minutes != null ? ` (${formatMinutes(t.sla_elapsed_minutes)})` : "") +
                          (t.sla_target_hours != null ? ` · quy định ${t.sla_target_hours}h` : "")
                        : undefined
                    }
                  >
                    {getOverdueMinutes(t) != null ? formatMinutes(getOverdueMinutes(t)!) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-rose-600">{fmt(t.penalty_points ?? 0)}</td>
                  <td className="px-3 py-2 text-right">{fmt(t.penalty_amount)}đ</td>
                  <td className="px-3 py-2 text-center">{FAULT_STATUS_LABEL[t.status] || "—"}</td>
                  <td className="px-3 py-2 text-center text-gray-500">{formatDateTime(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FaultTicketReportPage;
